<template>
  <div class="mb-4">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h3 class="container-section-header mb-0">
        {{ surveyType == "YES" ? "Engagement" : "Experience" }} Trends
      </h3>
      <router-link
        :to="{
          name: surveyType == 'RED' ? 'ViewAllSurveysRed' : 'ViewAllSurveysYes',
        }"
        class="d-flex align-items-center cursor-pointer"
      >
        <p class="view-all m-0 pe-2">Detailed Overview</p>
      </router-link>
    </div>
    <div class="p-4 container-section row g-0">
      <div
        class="line-graph position-relative col-10 position-relative"
        :class="'line-graph-' + graphClass"
      >
        <svg :class="graphClass" ref="lineGraph"></svg>
      </div>
      <div class="col-2 right-side-filter pt-4 ps-4">
        <div
          v-for="(item, idx) in dataset"
          :key="item.surveyGroupName"
          @mouseover="highlightTooltip(item.surveyGroupName, 'over')"
          @mouseout="highlightTooltip(item.surveyGroupName, 'out')"
        >
          <input
            type="checkbox"
            :id="surveyType + item.surveyGroupName + idx"
            :value="item.surveyGroupName"
            v-model="surveyGroupNames"
            class="d-none"
            @change="selectGroupName(item.surveyGroupName)"
          />
          <label
            class="mb-2 d-flex gap-2 cursor-pointer info-group-name"
            :for="surveyType + item.surveyGroupName + idx"
          >
            {{ item.surveyGroupName }}
          </label>
        </div>
        <div
          v-if="surveyGroupNames.length"
          @click="selectGroupName('clear')"
          class="info cursor-pointer"
        >
          X &nbsp;&nbsp; Clear Selection
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from "d3";
import colorsVariables from "@/utilities/colors-variables.js";

export default {
  name: "Trends",
  props: {
    surveyType: {
      type: String,
      required: true,
    },
    dataset: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      surveyGroupNames: [],
      height: 37,
    };
  },
  computed: {
    graphClass() {
      return `line-graph-2-${this.surveyType}`;
    },
    minVal() {
      return this.surveyType == "RED" ? -100 : -75;
    },
  },
  methods: {
    fullWidth() {
      return document.querySelector(`.line-graph`)?.clientWidth;
    },

    xAxisValues() {
      let quarters = ["R1", "R2", "R3", "R4"];
      let years = new Set();

      this.dataset.forEach((item) => {
        item.baseStats.forEach((baseStat) => {
          years.add(+baseStat.year);
        });
      });

      const sortedYearsArray = Array.from(years).sort((a, b) => a - b);

      let xAxisValues = [];
      sortedYearsArray.forEach((year) => {
        quarters.forEach((quarter) => {
          xAxisValues.push(`${year}/${quarter}`);
        });
      });

      return xAxisValues;
    },

    drawChart() {
      let rootFontSize = window
        .getComputedStyle(document.body)
        .getPropertyValue("font-size")
        .split("px")[0];
      let svg = d3
        .select(`.${this.graphClass}`)
        .attr("width", this.fullWidth())
        .attr("height", `${this.height}rem`)
        .style("transform", "translate(0, 0)");

      let yScaleRangeX = (this.height - 5) * rootFontSize;

      let xScale = d3
        .scaleBand()
        .range([130, this.fullWidth() - 30])
        .domain(this.xAxisValues())
        .paddingInner(1);

      let yScale = d3
        .scaleLinear()
        .domain([this.minVal, 150])
        .range([yScaleRangeX, 0]);

      svg
        .append("g")
        .attr("class", "y axis scale-axis-hide-tick")
        .attr("transform", "translate(50, 10)")
        .call(d3.axisLeft(yScale).tickValues([this.minVal, 150])); // Create an axis component with d3.axisLeft

      svg
        .append("g")
        .attr("class", "x axis")
        .style("transform", `translate(0, ${this.height - 3}rem)`)
        .call(d3.axisBottom(xScale))
        .selectAll("text")
        // .attr("transform", "translate(-10,10)rotate(-45)")
        .style("font-size", "0.9375rem")
        .style("text-anchor", "end");

      this.drawGradient(svg);
      //select path - three types: curveBasis,curveStep, curveCardinal, curveMonotoneX
      svg
        .selectAll(".line")
        .append("g")
        .attr("class", "line")
        .data(this.dataset)
        .enter()
        .append("path")
        .attr("d", function (d) {
          return d3
            .line()
            .x((d) => xScale(`${d.year}/R${d.quarter[1]}`))
            .y((d) => yScale(d.avgScore))
            .curve(d3.curveCardinal)(d.baseStats);
        })
        .attr("fill", "none")
        .attr(
          "class",
          (d) =>
            `${this.surveyType}-trends-line ${
              this.surveyType
            }-trends-line-${d.surveyGroupName.split(" ").join("")}`
        )
        .attr("stroke", "#fff")
        .attr("stroke-width", ".5px");

      let newdata = this.dataset
        .map((item) => {
          return [
            ...item.baseStats.map((state) => {
              return {
                ...state,
                surveyGroupName: item.surveyGroupName,
              };
            }),
          ];
        })
        .flat();

      let nodeInfo = svg.selectAll("circle").append("g").data(newdata).enter();

      nodeInfo
        .append("circle")
        .attr("r", "4px")
        .attr("cx", (d) => xScale(`${d.year}/R${d.quarter[1]}`))
        .attr("cy", (d) => yScale(d.avgScore))
        .style("fill", "#fff");

      newdata.forEach((item) => {
        d3.select(`.line-graph-${this.graphClass}`)
          .append("div")
          .attr(
            "class",
            `tooltip line-graph-tooltip ${
              this.surveyType
            }-tooltip-${item.surveyGroupName.split(" ").join("")}`
          )
          .style("position", "absolute")
          .style("opacity", 0)
          .style("top", `${yScale(item.avgScore) - 45}px`)
          .style("left", `${xScale(`${item.year}/R${item.quarter[1]}`) - 30}px`)
          .text(item.avgScore.toFixed(2));
      });
    },

    drawGradient(svg) {
      svg
        .append("defs")
        .append("linearGradient")
        .attr("id", "gradient")
        .attr("x1", "50%")
        .attr("y1", "0%")
        .attr("x2", "50%")
        .attr("y2", "100%")
        .selectAll("stop")
        .data([
          { offset: "0%", color: colorsVariables.colormaxi },
          { offset: "25%", color: colorsVariables.colorfany },
          { offset: "50%", color: colorsVariables.colornormaly },
          { offset: "75%", color: colorsVariables.colorsleepy },
          { offset: "100%", color: colorsVariables.colormadny },
        ])
        .enter()
        .append("stop")
        .attr("offset", (d) => {
          return d.offset;
        })
        .attr("stop-color", (d) => {
          return d.color;
        });

      // // Set the color for the start (0%)
      // linearGradient
      //   .append("stop")
      //   .attr("offset", "0%")
      //   .attr("stop-color", colorsVariables.colorEnthusiastic);

      // // Set the color for the end (100%)
      // linearGradient
      //   .append("stop")
      //   .attr("offset", "100%")
      //   .attr("stop-color", colorsVariables.colorNegative);

      svg
        .append("rect")
        .attr("height", `${this.height - 4}rem`)
        .attr("ry", "1.25rem")
        .style("fill", "url(#gradient)")
        .attr("width", +this.fullWidth() - 50)
        .style("transform", "translate(50px,0)");
    },

    selectGroupName(groupName) {
      if (groupName == "clear") {
        let items = d3.selectAll(`.${this.surveyType}-trends-line`);
        items
          .transition()
          .attr("stroke-width", itemWidth != "2px" ? ".5px" : "2px");
        this.surveyGroupNames = [];
        return;
      }
      let findItem = this.surveyGroupNames.find((item) => item == groupName);

      let item = d3.select(
        `.${this.surveyType}-trends-line-${groupName.split(" ").join("")}`
      );
      let itemWidth = item.attr("stroke-width");
      item.transition().attr("stroke-width", findItem ? "2px" : ".5px");
    },

    highlightTooltip(groupName, type) {
      let findItem = this.surveyGroupNames.find((item) => item == groupName);

      let line = d3.select(
        `.${this.surveyType}-trends-line-${groupName.split(" ").join("")}`
      );
      let items = d3.selectAll(
        `.${this.surveyType}-tooltip-${groupName.split(" ").join("")}`
      );
      if (type == "over") items.style("opacity", 1);
      else items.transition().style("opacity", 0);

      line
        .transition()
        .attr(
          "stroke-width",
          type == "over" ? "2px" : findItem ? "2px" : ".5px"
        );
    },
  },
  mounted() {
    this.drawChart();
  },
};
</script>

<style lang="scss" scoped>
.right-side-filter {
  height: 33rem;
  overflow: auto;
  padding-right: 1rem;
}
.info,
.info-group-name {
  border: $border-white-1;
  border-radius: 30px;
  padding: 0.44rem 1rem;
  font-weight: 600;
  font-size: 1rem;
  transition: $transition-200ms;
  padding-right: 1rem;
  width: fit-content;
  line-height: 1.3125rem;
}

label:hover {
  background-color: #fff;
  border-color: #000;
  color: #000;
}

input:checked + label.info-group-name {
  background-color: #fff;
  border-color: #000;
  color: #000;
}
.view-all {
  font-weight: $font-weight-semiBold;
  font-size: 1.5rem;
  line-height: 2rem;
}
</style>
